'use client';

import { useLocale } from 'next-intl';
import { forwardRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

import { RECAPTCHA_SITE_KEY } from '@/lib/const';
import { getRecaptchaLanguageFromLocale } from '@/lib/i18n/config';

/** Imperative handle exposed by {@link Recaptcha} — mirrors the widget's `reset()`. */
export type RecaptchaInstance = ReCAPTCHA;

export interface RecaptchaProps {
  /**
   * Called with the token when the challenge is solved, and with `null` when it
   * is cleared or the token expires (the token is single-use — reset the widget
   * before reusing it).
   */
  onChange: (token: string | null) => void;
}

/**
 * App wrapper around the third-party Google reCAPTCHA v2 widget.
 *
 * Centralises the site key and the locale-derived language so every form that
 * needs a captcha wires it identically — callers pass only `onChange` and,
 * optionally, a ref to call `reset()` after consuming the single-use token.
 * Swapping the underlying provider later touches this file alone.
 */
export const Recaptcha = forwardRef<RecaptchaInstance, RecaptchaProps>(function Recaptcha(
  { onChange },
  ref
) {
  const locale = useLocale();
  const recaptchaLang = getRecaptchaLanguageFromLocale(locale);

  return (
    <ReCAPTCHA
      // Remount when the language changes so the widget re-renders localized.
      key={recaptchaLang}
      ref={ref}
      sitekey={RECAPTCHA_SITE_KEY}
      hl={recaptchaLang}
      onChange={onChange}
      onExpired={() => onChange(null)}
    />
  );
});
